import _typeof from "@babel/runtime/helpers/typeof";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["remove"],
    _excluded2 = ["remove"],
    _excluded3 = ["arrayValidator", "label", "description", "fields", "defaultItem", "meta", "minItems", "maxItems", "noItemsMessage", "FormFieldGridProps", "FormControlProps", "buttonLabels", "validateOnMount", "isRequired", "helperText", "FormItemProps", "ArrayItemProps", "FieldsContainerProps", "RemoveContainerProps", "RemoveButtonProps", "FieldArrayRowProps", "FieldArrayRowCol", "FieldArrayHeaderProps", "FieldArrayLabelProps", "FieldArrayButtonsProps", "UndoButtonProps", "RedoButtonProps", "AddButtonProps", "FieldArrayDescriptionProps", "NoItemsMessageProps", "ErrorMessageProps"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { memo, useReducer } from 'react';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import useFieldApi from "@data-driven-forms/react-form-renderer/use-field-api";
import useFormApi from "@data-driven-forms/react-form-renderer/use-form-api";
import FieldArray from "@data-driven-forms/react-form-renderer/field-array";
import Row from "antd/es/row/index.js";
import Col from "antd/es/col/index.js";
import Button from "antd/es/button/index.js";
import Typography from "antd/es/typography/index.js";
import Space from "antd/es/space/index.js";
import { UndoOutlined, RedoOutlined } from '@ant-design/icons';
import FormGroup from '../form-group';
var ArrayItem = /*#__PURE__*/memo(function (_ref) {
  var fields = _ref.fields,
      fieldIndex = _ref.fieldIndex,
      name = _ref.name,
      remove = _ref.remove,
      length = _ref.length,
      minItems = _ref.minItems,
      removeLabel = _ref.removeLabel,
      ArrayItemProps = _ref.ArrayItemProps,
      FieldsContainerProps = _ref.FieldsContainerProps,
      RemoveContainerProps = _ref.RemoveContainerProps,
      RemoveButtonProps = _ref.RemoveButtonProps;

  var _useFormApi = useFormApi(),
      renderForm = _useFormApi.renderForm;

  var editedFields = fields.map(function (field, index) {
    var computedName = field.name ? "".concat(name, ".").concat(field.name) : name;
    return _objectSpread(_objectSpread({}, field), {}, {
      name: computedName,
      key: "".concat(computedName, "-").concat(index)
    });
  });
  return /*#__PURE__*/React.createElement(Row, ArrayItemProps, /*#__PURE__*/React.createElement(Col, _extends({
    span: 24
  }, FieldsContainerProps), renderForm([editedFields])), /*#__PURE__*/React.createElement(Col, _extends({
    span: 24
  }, RemoveContainerProps), /*#__PURE__*/React.createElement(Button, _extends({
    type: "primary",
    danger: true
  }, RemoveButtonProps, {
    onClick: function onClick() {
      return remove(fieldIndex);
    },
    disabled: length <= minItems
  }), removeLabel)));
}, function (_ref2, _ref3) {
  var _prevRemove = _ref2.remove,
      prev = _objectWithoutProperties(_ref2, _excluded);

  var _nextRemove = _ref3.remove,
      next = _objectWithoutProperties(_ref3, _excluded2);

  return isEqual(prev, next);
});
ArrayItem.propTypes = {
  name: PropTypes.string,
  fieldIndex: PropTypes.number.isRequired,
  fields: PropTypes.arrayOf(PropTypes.object),
  remove: PropTypes.func.isRequired,
  length: PropTypes.number,
  minItems: PropTypes.number,
  removeLabel: PropTypes.node.isRequired,
  ArrayItemProps: PropTypes.object.isRequired,
  FieldsContainerProps: PropTypes.object.isRequired,
  RemoveContainerProps: PropTypes.object.isRequired,
  RemoveButtonProps: PropTypes.object.isRequired
};
var defaultButtonLabels = {
  add: 'ADD',
  remove: 'REMOVE'
};
var initialState = {
  index: 0,
  history: []
};
export var reducer = function reducer(state, _ref4) {
  var type = _ref4.type,
      action = _ref4.action;

  switch (type) {
    case 'redo':
      return _objectSpread(_objectSpread({}, state), {}, {
        index: state.index + 1
      });

    case 'action':
      return {
        index: state.index + 1,
        history: [].concat(_toConsumableArray(state.history.slice(0, state.index)), [action])
      };

    case 'undo':
      return _objectSpread(_objectSpread({}, state), {}, {
        index: state.index - 1
      });

    case 'resetHistory':
      return _objectSpread(_objectSpread({}, state), {}, {
        history: state.history.slice(0, state.index)
      });

    default:
      return state;
  }
};

var DynamicArray = function DynamicArray(_ref5) {
  var props = _extends({}, _ref5);

  var _useFieldApi = useFieldApi(props),
      arrayValidator = _useFieldApi.arrayValidator,
      label = _useFieldApi.label,
      description = _useFieldApi.description,
      formFields = _useFieldApi.fields,
      defaultItem = _useFieldApi.defaultItem,
      meta = _useFieldApi.meta,
      minItems = _useFieldApi.minItems,
      maxItems = _useFieldApi.maxItems,
      noItemsMessage = _useFieldApi.noItemsMessage,
      FormFieldGridProps = _useFieldApi.FormFieldGridProps,
      FormControlProps = _useFieldApi.FormControlProps,
      buttonLabels = _useFieldApi.buttonLabels,
      validateOnMount = _useFieldApi.validateOnMount,
      isRequired = _useFieldApi.isRequired,
      helperText = _useFieldApi.helperText,
      FormItemProps = _useFieldApi.FormItemProps,
      ArrayItemProps = _useFieldApi.ArrayItemProps,
      FieldsContainerProps = _useFieldApi.FieldsContainerProps,
      RemoveContainerProps = _useFieldApi.RemoveContainerProps,
      RemoveButtonProps = _useFieldApi.RemoveButtonProps,
      FieldArrayRowProps = _useFieldApi.FieldArrayRowProps,
      FieldArrayRowCol = _useFieldApi.FieldArrayRowCol,
      FieldArrayHeaderProps = _useFieldApi.FieldArrayHeaderProps,
      FieldArrayLabelProps = _useFieldApi.FieldArrayLabelProps,
      FieldArrayButtonsProps = _useFieldApi.FieldArrayButtonsProps,
      UndoButtonProps = _useFieldApi.UndoButtonProps,
      RedoButtonProps = _useFieldApi.RedoButtonProps,
      AddButtonProps = _useFieldApi.AddButtonProps,
      FieldArrayDescriptionProps = _useFieldApi.FieldArrayDescriptionProps,
      NoItemsMessageProps = _useFieldApi.NoItemsMessageProps,
      ErrorMessageProps = _useFieldApi.ErrorMessageProps,
      rest = _objectWithoutProperties(_useFieldApi, _excluded3);

  var _useReducer = useReducer(reducer, initialState),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      state = _useReducer2[0],
      dispatch = _useReducer2[1];

  var combinedButtonLabels = _objectSpread(_objectSpread({}, defaultButtonLabels), buttonLabels);

  var dirty = meta.dirty,
      submitFailed = meta.submitFailed,
      error = meta.error,
      submitError = meta.submitError;
  var isError = (dirty || submitFailed) && error && typeof error === 'string';
  return /*#__PURE__*/React.createElement(FormGroup, {
    meta: _objectSpread(_objectSpread({}, meta), {}, {
      error: _typeof(error) === 'object' ? error.name : error
    }),
    validateOnMount: validateOnMount,
    helperText: helperText,
    FormItemProps: FormItemProps,
    isRequired: isRequired
  }, /*#__PURE__*/React.createElement(FieldArray, {
    key: rest.input.name,
    name: rest.input.name,
    validate: arrayValidator
  }, function (_ref6) {
    var _ref6$fields = _ref6.fields,
        map = _ref6$fields.map,
        _ref6$fields$value = _ref6$fields.value,
        value = _ref6$fields$value === void 0 ? [] : _ref6$fields$value,
        push = _ref6$fields.push,
        remove = _ref6$fields.remove;

    var pushWrapper = function pushWrapper() {
      dispatch({
        type: 'resetHistory'
      });
      push(defaultItem);
    };

    var removeWrapper = function removeWrapper(index) {
      dispatch({
        type: 'action',
        action: {
          action: 'remove',
          value: value[index]
        }
      });
      remove(index);
    };

    var undo = function undo() {
      push(state.history[state.index - 1].value);
      dispatch({
        type: 'undo'
      });
    };

    var redo = function redo() {
      remove(value.length - 1);
      dispatch({
        type: 'redo'
      });
    };

    return /*#__PURE__*/React.createElement(Row, _extends({
      gutter: [0, 16]
    }, FieldArrayRowProps), /*#__PURE__*/React.createElement(Col, _extends({
      span: 24
    }, FieldArrayRowCol), /*#__PURE__*/React.createElement(Row, _extends({
      justify: "space-between"
    }, FieldArrayHeaderProps), /*#__PURE__*/React.createElement(Col, null, label && /*#__PURE__*/React.createElement(Typography.Title, _extends({
      level: 4
    }, FieldArrayLabelProps), label)), /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement(Space, FieldArrayButtonsProps, /*#__PURE__*/React.createElement(Button, _extends({
      type: "default",
      icon: /*#__PURE__*/React.createElement(UndoOutlined, null)
    }, UndoButtonProps, {
      onClick: undo,
      disabled: state.index === 0
    })), /*#__PURE__*/React.createElement(Button, _extends({
      type: "default",
      icon: /*#__PURE__*/React.createElement(RedoOutlined, null)
    }, RedoButtonProps, {
      onClick: redo,
      disabled: state.index === state.history.length
    })), /*#__PURE__*/React.createElement(Button, _extends({
      type: "primary"
    }, AddButtonProps, {
      onClick: pushWrapper,
      disabled: value.length >= maxItems
    }), combinedButtonLabels.add))))), description && /*#__PURE__*/React.createElement(Col, {
      span: 24
    }, /*#__PURE__*/React.createElement(Typography.Text, FieldArrayDescriptionProps, description)), /*#__PURE__*/React.createElement(Col, {
      span: 24
    }, /*#__PURE__*/React.createElement(Row, {
      gutter: [0, 16]
    }, value.length <= 0 ? typeof noItemsMessage === 'string' ? /*#__PURE__*/React.createElement(Typography.Text, NoItemsMessageProps, noItemsMessage) : /*#__PURE__*/React.cloneElement(noItemsMessage, NoItemsMessageProps) : map(function (name, index) {
      return /*#__PURE__*/React.createElement(Col, {
        span: 24,
        key: name
      }, /*#__PURE__*/React.createElement(ArrayItem, {
        fields: formFields,
        name: name,
        fieldIndex: index,
        remove: removeWrapper,
        length: value.length,
        minItems: minItems,
        removeLabel: combinedButtonLabels.remove,
        ArrayItemProps: ArrayItemProps,
        FieldsContainerProps: FieldsContainerProps,
        RemoveContainerProps: RemoveContainerProps,
        RemoveButtonProps: RemoveButtonProps
      }));
    }))), (isError || submitError) && /*#__PURE__*/React.createElement(Col, {
      span: 12
    }, /*#__PURE__*/React.createElement(Typography.Text, _extends({
      type: "danger"
    }, ErrorMessageProps), _typeof(error) === 'object' ? error.name : error || submitError)));
  }));
};

DynamicArray.propTypes = {
  label: PropTypes.node,
  description: PropTypes.node,
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  defaultItem: PropTypes.any,
  minItems: PropTypes.number,
  maxItems: PropTypes.number,
  noItemsMessage: PropTypes.node,
  buttonLabels: PropTypes.object,
  // customization props
  FormItemProps: PropTypes.object,
  ArrayItemProps: PropTypes.object,
  FieldsContainerProps: PropTypes.object,
  RemoveContainerProps: PropTypes.object,
  RemoveButtonProps: PropTypes.object,
  FieldArrayRowProps: PropTypes.object,
  FieldArrayRowCol: PropTypes.object,
  FieldArrayHeaderProps: PropTypes.object,
  FieldArrayLabelProps: PropTypes.object,
  FieldArrayButtonsProps: PropTypes.object,
  UndoButtonProps: PropTypes.object,
  RedoButtonProps: PropTypes.object,
  AddButtonProps: PropTypes.object,
  FieldArrayDescriptionProps: PropTypes.object,
  NoItemsMessageProps: PropTypes.object,
  ErrorMessageProps: PropTypes.object
};
DynamicArray.defaultProps = {
  maxItems: Infinity,
  minItems: 0,
  noItemsMessage: 'No items added',
  FormItemProps: {},
  ArrayItemProps: {},
  FieldsContainerProps: {},
  RemoveContainerProps: {},
  RemoveButtonProps: {},
  FieldArrayRowProps: {},
  FieldArrayRowCol: {},
  FieldArrayHeaderProps: {},
  FieldArrayLabelProps: {},
  FieldArrayButtonsProps: {},
  UndoButtonProps: {},
  RedoButtonProps: {},
  AddButtonProps: {},
  FieldArrayDescriptionProps: {},
  NoItemsMessageProps: {},
  ErrorMessageProps: {}
};
export default DynamicArray;